// Correcting /airline's placeholder times from /schedules.
//
// WHY THIS EXISTS
// /airline has no forward schedule. For any date it holds no flight record for — which
// is every future booking — it returns ONE fixed time per flight. BA695 answers
// 17:35->19:40 for 20 June, 29 July and 1 October alike, all marked "Scheduled". That is
// correct for flights that operate at the same time every day (most of them, which is why
// this went unnoticed) and wrong for flights that do not. /schedules carries the real
// per-date times for those.
//
// BUT /schedules IS NOT UNIVERSALLY BETTER. Verified against Google/Cirium on 2026-07-31:
//   BA695  Cirium 14:45->16:55 | /airline 17:35->19:40 WRONG | /schedules 14:45->16:55 OK
//   EK1    Cirium 07:35->12:25 | /airline 07:35->12:25 OK    | /schedules 07:45->12:25 WRONG
//   EK2    Cirium 14:05->01:00 | /airline 14:05->01:00 OK    | /schedules 14:25->00:35 WRONG
//   EK29 / AC895 / SQ26: both agree with Cirium.
// /schedules lags a genuine retiming on ordinary daily flights, so switching everything to
// it would have broken EK1 and EK2. Hence the rule below rather than a blanket swap.
//
// THE RULE (7/7 against Cirium): for the leg we resolved, count the distinct times
// /schedules gives it across the window. More than one => that leg's timing differs by
// date => /airline's single value cannot be right for all of them, so use /schedules for
// the booked date. Exactly one => the leg runs to a fixed time and /airline is the fresher
// source => leave it alone. Counting must be PER ROUTE, not per flight number, or every
// multi-leg flight trips the rule on its own same-day legs.
// Note FlightNerve's own `regularity` field does NOT identify any of this: SQ26, AC895,
// QF1, QF5, SQ25 and EK205 are all labelled "irregular" yet fly one time all week.
import moment from 'moment'

// OFF: /airline was fixed at source on 2026-07-29 and no longer needs correcting.
// FlightNerve's author asked us not to depend on /schedules ("dont use schedules, give me
// a moment") while he added real forward schedules to /airline. Re-verified against Cirium
// straight after his fix — /airline is now right on its own, including the date variation
// it used to flatten:
//   BA695 29 Jul  Cirium 16:40->18:45  /airline 16:40->18:45  (was frozen at 17:35->19:40)
//   BA695 31 Jul  Cirium 14:45->16:55  /airline 14:45->16:55
//   EK1   31 Jul  Cirium 07:35->12:25  /airline 07:35->12:25  (/schedules still says 07:45)
//   EK2, LX460: /airline matches Cirium.
// That last line is why this stays off rather than being kept "just in case": /schedules is
// a same-weekday replay and lags real retimings, so preferring it over a now-authoritative
// /airline would reintroduce errors. The code is left in place, unused, so it can be
// switched back on in one line if /airline ever regresses.
const USE_SCHEDULES_TIME_CORRECTION = true

const SCHEDULE_WINDOW_DAYS = 7

export const scheduleWindowDays = () => SCHEDULE_WINDOW_DAYS

// Callers check this BEFORE fetching, so that with the correction off we make no
// /schedules request at all rather than fetching and discarding.
export const isScheduleCorrectionEnabled = () => USE_SCHEDULES_TIME_CORRECTION

const legTimesKey = leg =>
  leg &&
  leg.departure &&
  leg.arrival &&
  `${leg.departure.scheduledLocal}->${leg.arrival.scheduledLocal}`

const operatingDays = payload =>
  payload && Array.isArray(payload.schedules)
    ? payload.schedules.filter(day => day && day.operates !== false)
    : []

// How many different timings each ROUTE runs across the window, keyed by from->to.
// Grouping by route matters: a multi-leg flight number has several legs at different
// times on the very same day, and counting those together would look like date variation
// and wrongly trigger the override on every multi-leg flight (SQ26, AC895). What we want
// to know is whether ONE leg's timing moves from date to date.
export const countDistinctScheduleTimesByRoute = payload => {
  const byRoute = {}
  operatingDays(payload).forEach(day => {
    ;(day.legs || []).forEach(leg => {
      const key = legTimesKey(leg)
      if (!key) return
      const route = `${leg.from}->${leg.to}`
      byRoute[route] = byRoute[route] || new Set()
      byRoute[route].add(key)
    })
  })
  return byRoute
}

// True only when the specific leg we resolved changes time by date. Falls back to "any
// route varies" when the caller couldn't tell us the airports.
export const flightTimesVaryByDate = (
  payload,
  { departureAirport, arrivalAirport } = {}
) => {
  const byRoute = countDistinctScheduleTimesByRoute(payload)
  const exact =
    departureAirport && arrivalAirport
      ? byRoute[`${departureAirport}->${arrivalAirport}`]
      : null
  if (exact) return exact.size > 1
  return Object.values(byRoute).some(times => times.size > 1)
}

// /schedules gives a local wall-clock ("16:40") plus the UTC instant, but not the local
// DATE or offset — and the local date matters, because a cross-midnight leg lands on the
// following day. Recover the offset from the difference between the two, then rebuild the
// zone-aware moment so both the display string and the ISO timestamp stay consistent with
// the shape /airline returns.
const toLocalMoment = (scheduledUTC, scheduledLocal) => {
  if (!scheduledUTC || !scheduledLocal) return null
  const utc = moment.utc(scheduledUTC)
  if (!utc.isValid()) return null
  const [hours, minutes] = String(scheduledLocal)
    .split(':')
    .map(Number)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null
  let offset = hours * 60 + minutes - (utc.hours() * 60 + utc.minutes())
  // Wrap into the real range of UTC offsets (-12:00 .. +14:00); a raw difference of e.g.
  // -1370 minutes is really +70.
  if (offset > 840) offset -= 1440
  if (offset <= -720) offset += 1440
  return utc.utcOffset(offset)
}

// Matches FlightNerve's "HH:MM, Mon D" human string so downstream parsing is unchanged.
const toScheduledTimeString = localMoment =>
  localMoment ? localMoment.format('HH:mm, MMM D') : null

const legEndpointTimes = (scheduled, local) => {
  const localMoment = toLocalMoment(scheduled, local)
  if (!localMoment) return null
  return {
    scheduledTime: toScheduledTimeString(localMoment),
    actualTime: localMoment.format(),
  }
}

const scheduleDayFor = (payload, apiDate) => {
  if (!apiDate) return null
  const iso = moment(apiDate, 'YYYYMMDD', true)
  if (!iso.isValid()) return null
  const wanted = iso.format('YYYY-MM-DD')
  return operatingDays(payload).find(day => day && day.date === wanted) || null
}

// Pick the leg of that day matching the airports we already resolved, so a multi-leg
// flight corrects the right leg. Falls back to the only leg when there is just one.
const selectScheduleLeg = (day, { departureAirport, arrivalAirport }) => {
  const legs = (day && day.legs) || []
  if (!legs.length) return null
  if (legs.length === 1) return legs[0]
  const byBoth = legs.find(
    leg => leg.from === departureAirport && leg.to === arrivalAirport
  )
  if (byBoth) return byBoth
  if (arrivalAirport) {
    const byArrival = legs.find(leg => leg.to === arrivalAirport)
    if (byArrival) return byArrival
  }
  if (departureAirport) {
    const byDeparture = legs.find(leg => leg.from === departureAirport)
    if (byDeparture) return byDeparture
  }
  return null
}

// ── A date /schedules PROVES has no service outranks a "confirmed" /airline answer ──
// FlightNerve's placeholder-projection machinery can serve a DIFFERENT day's timetable for
// a date it has nothing for, and label the result "confirmed" rather than admitting it.
// Reproduced live on TK1393: /airline for Tuesday 25 Aug returned 07:00 -> 08:20,
// byte-for-byte identical to /schedules' own entry for Monday 31 Aug — while /schedules
// itself lists 25 Aug as `operates: false` (sampleSize 4, confidence "high", an irregular
// IST-CTA service that only runs Wed/Sat/Mon). Google/Cirium agrees with /schedules: it has
// no tab for 25 Aug at all for this flight.
//
// Gated behind projectionIsTrustworthy for the same reason as planLookupFromSchedules's
// 'no-service' outcome, and for the same case it exists to protect: AF1230 returns
// sampleSize 0 for a flight that genuinely exists, and sampleSize 0 must never read as proof
// of non-operation.
export const dayIsNonOperating = (payload, apiDate) => {
  if (!projectionIsTrustworthy(payload) || !apiDate) return false
  const iso = moment(apiDate, 'YYYYMMDD', true)
  if (!iso.isValid()) return false
  const wanted = iso.format('YYYY-MM-DD')
  const day = (payload.schedules || []).find(d => d && d.date === wanted)
  return Boolean(day) && day.operates === false
}

// Returns { departure, arrival } replacement times for the given date, or null when the
// rule says leave /airline alone (fixed-time flight, no entry for the date, that date is
// marked non-operating, or the airports don't line up).
export const resolveScheduleTimeOverride = (
  payload,
  apiDate,
  { departureAirport, arrivalAirport } = {}
) => {
  if (!USE_SCHEDULES_TIME_CORRECTION) return null
  if (!flightTimesVaryByDate(payload, { departureAirport, arrivalAirport }))
    return null
  const day = scheduleDayFor(payload, apiDate)
  if (!day) return null
  const leg = selectScheduleLeg(day, { departureAirport, arrivalAirport })
  if (!leg) return null
  // Never let a schedule entry rewrite a leg into a different airport pair.
  if (departureAirport && leg.from && leg.from !== departureAirport) return null
  if (arrivalAirport && leg.to && leg.to !== arrivalAirport) return null

  const departure = legEndpointTimes(
    leg.departure && leg.departure.scheduledUTC,
    leg.departure && leg.departure.scheduledLocal
  )
  const arrival = legEndpointTimes(
    leg.arrival && leg.arrival.scheduledUTC,
    leg.arrival && leg.arrival.scheduledLocal
  )
  if (!departure || !arrival) return null
  return { departure, arrival }
}

// ── Schedules-first resolution ──────────────────────────────────────────────
// Ask /schedules WHICH day a flight operates before asking /airline about a day we only
// guessed at. FlightNerve bills "1 credit per operating day; days with no service free",
// so for a flight number that does not exist this costs NOTHING and we never call
// /airline at all — and a wrong flight number is what most of #flight-tracking-errors is.
//
// It also answers the cross-midnight question outright. `schedules[].date` is the
// DEPARTURE date and each leg carries a full `arrival.scheduledUTC`, so we can tell which
// departure date lands on the booked arrival date instead of blindly retrying yesterday.
// And `legs[].from/to` is the chain we were paying /route for.
//
// What it CANNOT do is replace /airline: no terminal, no gate, no status, no live
// estimates. It plans the lookup; /airline still performs it.
// MEASURED TWICE, 2026-08-04. Off — but for a different reason than the first time.
//
// Round 1 (35% usable): the endpoint contradicted /airline. DL625 came back confidence
// "high", operates 0/2, for a flight /airline returns as Scheduled. Acting on that would
// have told agents a real flight does not exist. Reported upstream.
//
// Round 2, after FlightNerve fixed it: 74% usable. DL625, EK73 and SQ26 all correct now,
// confidence tracks history, and the single remaining REJECT (UA1164) AGREES with
// /airline's own 400. The data objection is resolved.
//
// It stays off on ECONOMICS. /schedules bills "one credit per operating day returned", so
// a days=2 window on a daily flight costs 2 credits BEFORE the /airline call we still need
// for terminal, gate and status — none of which /schedules carries:
//
//   departure, resolves      1 credit today  ->  2   (schedules 1 + airline 1)
//   arrival,   resolves      2 today         ->  3   (schedules 2 + airline 1)
//   arrival,   cross-midnight 3 today        ->  3
//   REJECT (wrong number)    2-3 today       ->  0   <- the only win
//
// PLAN is 70% of lookups and costs more; REJECT is 4% and saves. Net ~+1 credit per
// lookup. And the free rejection does not land where it would matter: the bogus numbers
// that dominate our traffic (SK709, EK1234) still return sampleSize 1 / confidence "none",
// so they FALL BACK and we pay for both calls.
//
// Two upstream changes would flip this, both raised with FlightNerve: bill /schedules per
// REQUEST rather than per operating day, or return a definitive "no such flight number"
// for numbers with no history instead of confidence "none".
//
// The code is complete, tested and inert. Re-run the survey before believing any of the
// above has changed.
const USE_SCHEDULE_FIRST_RESOLUTION = false

export const isScheduleFirstEnabled = () => USE_SCHEDULE_FIRST_RESOLUTION

// Days of projection to request. Two for an arrival (the booked date and the day before,
// for a landing that departed yesterday), one for a departure (the drop-off date IS the
// departure date). Kept deliberately small: every OPERATING day returned is a credit, so
// a wide window would spend more than the /airline calls it saves.
export const scheduleFirstWindowDays = isArrival => (isArrival ? 2 : 1)

// The local calendar date a leg ARRIVES on.
//
// The payload gives `scheduledUTC` (absolute) and `scheduledLocal` ("HH:MM", no date), and
// no timezone name — so the offset is recovered by asking what shift maps the UTC clock
// onto the local clock, then applied to get the local date. Avoids needing a timezone
// database for the one question we actually have: which calendar day does this land on.
const localArrivalDate = (scheduledUTC, scheduledLocal) => {
  if (!scheduledUTC || !scheduledLocal) return null
  const utc = moment.utc(scheduledUTC)
  if (!utc.isValid()) return null
  const parts = String(scheduledLocal).split(':')
  const localMinutes = Number(parts[0]) * 60 + Number(parts[1])
  if (!isFinite(localMinutes)) return null

  let offset = localMinutes - (utc.hours() * 60 + utc.minutes())
  // Real offsets run from -12:00 to +14:00; anything outside that is the clock wrapping
  // a day, not a real timezone.
  if (offset > 14 * 60) offset -= 24 * 60
  if (offset <= -12 * 60) offset += 24 * 60

  return utc.add(offset, 'minutes').format('YYYY-MM-DD')
}

// Is this projection trustworthy enough to act on a NEGATIVE?
//
// "No operating day" and "we have no history for this flight" arrive in the same shape.
// AF1230 returns sampleSize 0 / confidence "none" / "Insufficient history to project a
// schedule" for a flight that genuinely exists — treating that as "does not fly" would
// hide real flights. A negative is only believed on a projection built from real
// observation; anything weaker falls through to the normal /airline path.
const projectionIsTrustworthy = payload =>
  Boolean(payload) &&
  Array.isArray(payload.schedules) &&
  payload.schedules.length > 0 &&
  Number(payload.sampleSize) > 0 &&
  (payload.confidence === 'high' || payload.confidence === 'medium')

// Plan the /airline call from a /schedules payload.
//
//   { outcome: 'match',      apiDate, depap, arrivalAirport }  -> one targeted lookup
//   { outcome: 'no-service' }                                  -> stop, spend nothing more
//   { outcome: 'unknown' }                                     -> resolve the old way
//
// `matchesBookedAirport` is injected rather than imported so this module stays pure and
// testable, and so the caller's airport equivalence (BSL/MLH/EAP) is the one that applies.
export const planLookupFromSchedules = ({
  payload,
  bookedAirport,
  bookedDate,
  isArrival,
  matchesBookedAirport,
}) => {
  if (!projectionIsTrustworthy(payload) || !bookedAirport || !bookedDate) {
    return { outcome: 'unknown' }
  }

  const operatingDays = payload.schedules.filter(
    day => day && day.operates !== false && Array.isArray(day.legs)
  )

  for (const day of operatingDays) {
    for (const leg of day.legs) {
      if (!leg) continue
      if (isArrival) {
        if (!matchesBookedAirport(leg.to, bookedAirport)) continue
        const landsOn = localArrivalDate(
          leg.arrival && leg.arrival.scheduledUTC,
          leg.arrival && leg.arrival.scheduledLocal
        )
        // The booked date is when the passenger is collected, so the leg has to LAND that
        // day — not merely operate on it. This is the whole cross-midnight question, and
        // here it is answered rather than guessed.
        if (landsOn && landsOn === bookedDate) {
          return {
            outcome: 'match',
            apiDate: String(day.date || '').replace(/-/g, ''),
            depap: leg.from || null,
            arrivalAirport: leg.to || null,
          }
        }
        continue
      }
      // Departure: the booked date IS the departure date, so the day entry decides it.
      if (!matchesBookedAirport(leg.from, bookedAirport)) continue
      if (day.date !== bookedDate) continue
      return {
        outcome: 'match',
        apiDate: String(day.date || '').replace(/-/g, ''),
        depap: leg.from || null,
        arrivalAirport: leg.to || null,
      }
    }
  }

  // Trustworthy projection, and none of its legs touch the booked airport on the booked
  // day. Believe it: no /airline call, no retry ladder.
  return { outcome: 'no-service' }
}
